import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store } from '@/store';
import { initializeAuth, validateToken, refreshToken as refreshTokenAction } from '@/store/slices/authSlice';
import { apiConfig } from '@/config/apiConfig';
import { authService } from '@/services/authService';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { LoginForm } from '@/features/auth/LoginForm';
import { Dashboard } from '@/features/dashboard/Dashboard';
import { UserList } from '@/features/user-management/UserList';
import { OnboardingLayout, CasesPage, NewCasePage, CandidateDetailPage } from '@/features/user-management/onboarding';
import {
  CandidatePortalLayout,
  CandidatePortalPage,
  InviteEntryPage,
  InviteExpiredPage,
} from '@/features/candidate-portal';
import { HonorariumManagement } from '@/features/honorarium/HonorariumManagement';
import { LocationTrackingLayout, LiveTrackingPage, ReportsPage, LocationUserManagementPage } from '@/features/location-tracking';
import { AssetManagement, PhotoSources, VideoSources, AssetLibrary } from '@/features/asset-management';
import { VisualGalleries } from '@/features/asset-management/visual-galleries/VisualGalleries';
import { ImpactDataPage, ResourceReviewPage } from '@/features/impact-data';
import { LocationManagement } from '@/features/location-management';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    },
  },
});

const Settings = () => (
  <div className="text-center py-12">
    <h2 className="text-2xl font-bold text-rgf-gray-900 mb-4">Settings</h2>
    <p className="text-rgf-gray-600">Settings interface will be implemented here.</p>
  </div>
);

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-rgf-gray-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-rgf-gray-900 mb-4">404</h1>
      <p className="text-rgf-gray-600 mb-8">Page not found</p>
      <a
        href="/dashboard"
        className="btn-primary"
      >
        Go to Dashboard
      </a>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    
    // Initialize auth state on app startup
    const token = localStorage.getItem(apiConfig.auth.tokenKey) || sessionStorage.getItem(apiConfig.auth.tokenKey);
    
    
    // Always initialize auth first
    store.dispatch(initializeAuth());
    
    if (token) {
      // Parse token to check if it's still valid locally before making API call
      const tokenData = authService.parseToken(token);
      
      if (tokenData && tokenData.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (tokenData.exp > currentTime) {
          // Token is not expired, validate with backend
          store.dispatch(validateToken());
        } else {
          // Token is expired, try refresh
          const refreshTokenValue = localStorage.getItem(apiConfig.auth.refreshTokenKey) || sessionStorage.getItem(apiConfig.auth.refreshTokenKey);
          if (refreshTokenValue) {
            store.dispatch(refreshTokenAction());
          }
        }
      } else {
        authService.clearTokens();
      }
    } else {
    }
  }, []);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginForm />} />

              {/* Public candidate-onboarding portal — invite-token authenticated.
                  Lives outside AppLayout / ProtectedRoute so candidates never
                  hit the backoffice login redirect. */}
              <Route path="/onboarding/invite" element={<CandidatePortalLayout />}>
                <Route index element={<CandidatePortalPage />} />
                <Route path="expired" element={<InviteExpiredPage />} />
                <Route path=":token" element={<InviteEntryPage />} />
              </Route>

              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }>
                {/* Dashboard - accessible to all authenticated users */}
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                
                {/* User Management - temporarily removed ADMIN requirement for testing */}
                <Route path="users" element={
                  <ProtectedRoute>
                    <UserList />
                  </ProtectedRoute>
                } />

                {/* Onboarding (Changemaker / Block Changemaker pre-onboarding) */}
                <Route path="onboarding" element={
                  <ProtectedRoute requiredRoles={['ADMIN', 'ONBOARDING.PROGRAM_ASSOCIATE', 'ONBOARDING.PNC_OFFICER','COLLABORATION_RENEW.FINANCE']}>
                    <OnboardingLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="candidates" replace />} />
                  <Route path="candidates" element={<CasesPage />} />
                  <Route path="candidates/new" element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'ONBOARDING.PROGRAM_ASSOCIATE']}>
                      <NewCasePage />
                    </ProtectedRoute>
                  } />
                  <Route path="candidates/:id" element={<CandidateDetailPage />} />
                </Route>

                {/* Honorarium Management */}
                <Route path="honorarium" element={
                  <ProtectedRoute>
                    <HonorariumManagement />
                  </ProtectedRoute>
                } />
                
                {/* Location Tracking - requires at least one location role */}
                <Route path="location-tracking" element={
                  <ProtectedRoute requiredRoles={['ADMIN', 'LOCATION.TRACKING_VIEWER', 'LOCATION.REPORT_VIEWER', 'LOCATION.USER_MANAGER']}>
                    <LocationTrackingLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="live" replace />} />
                  <Route path="live" element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'LOCATION.TRACKING_VIEWER']}>
                      <LiveTrackingPage />
                    </ProtectedRoute>
                  } />
                  <Route path="reports" element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'LOCATION.REPORT_VIEWER']}>
                      <ReportsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="users" element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'LOCATION.USER_MANAGER']}>
                      <LocationUserManagementPage />
                    </ProtectedRoute>
                  } />
                </Route>

                {/* Asset Management - temporarily removed role requirement for testing */}
                <Route path="asset-management" element={
                  <ProtectedRoute>
                    <AssetManagement />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="photo-sources" replace />} />
                  <Route path="photo-sources" element={<PhotoSources />} />
                  <Route path="video-sources" element={<VideoSources />} />
                  <Route path="asset-library" element={<AssetLibrary />} />
                  <Route path="galleries/*" element={<VisualGalleries />} />
                </Route>
                
                {/* Impact Data (maker-checker) — one entry per program.
                    Program access is enforced inside the page against
                    JWT roles (prefix match on program codes) since
                    ProtectedRoute only supports exact role strings. */}
                <Route path="programs/:programCode/impact-data" element={
                  <ProtectedRoute>
                    <ImpactDataPage />
                  </ProtectedRoute>
                } />
                <Route path="programs/:programCode/impact-data/:resource" element={
                  <ProtectedRoute>
                    <ResourceReviewPage />
                  </ProtectedRoute>
                } />

                {/* Location Management (Punjab master + custom locations).
                    Browse endpoints only need console auth, but writes
                    (custom location, cache refresh) require ADMIN — the
                    page gates the write CTAs on that role internally. */}
                <Route path="locations" element={
                  <ProtectedRoute requiredRoles={['ADMIN']}>
                    <LocationManagement />
                  </ProtectedRoute>
                } />

                {/* Settings - requires ADMIN role */}
                <Route path="settings" element={
                  <ProtectedRoute requiredRoles={['ADMIN']}>
                    <Settings />
                  </ProtectedRoute>
                } />
              </Route>
              
              {/* Fallback for unknown routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;